import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { bool, func, shape } from 'prop-types';
import { useDispatch } from 'react-redux';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import { closeSnack, createSnack } from 'snackbar/snackbarSlice';
import { formatNumber } from 'utils/helpers/format';
import usePersistentState from 'hooks/usePersistentState';
import {
  BulkActionFooter,
  MDialog,
  PersistentServerSideDataGrid,
  useCustomizablePopover,
} from 'common';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import {
  useBulkActivateCustomerAuthorizationsMutation,
  useBulkDeactivateCustomerAuthorizationsMutation,
  useGetCustomerAuthorizationsListQuery,
  useLazyGetCustomerAuthorizationsExportListQuery,
  useToggleCustomerAuthorizationActivationMutation,
} from './customerAuthGridEndpoints';
import CustomerAuthorizationBulkSnackbar from './CustomerAuthorizationBulkSnackbar';

const PAGE_SIZE_STORAGE_KEY = 'customerAuthorizationsGridPageSize';
const DEFAULT_PAGE_SIZE = 50;
const MAX_VISIBLE_PRODUCTS = 10;
const MAX_VISIBLE_LOCATIONS = 10;
const MAX_VISIBLE_LOADING_CONTROL_NUMBERS = 10;
const MAX_VISIBLE_DESTINATIONS = 10;

const POPOVER_TEXT_SX = {
  color: 'text.primary',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const POPOVER_TEXT_WITH_MAX_WIDTH_SX = {
  ...POPOVER_TEXT_SX,
  maxWidth: 250,
};

const SORT_COLUMN_MAP = {
  activeStatus: 'Status',
  authorizationType: 'AuthorizationType',
  class: 'Class',
  controlType: 'ControlType',
  customerId: 'CustomerId',
  customerName: 'CustomerName',
};

const normalizeStatusValue = status => {
  const normalizedStatus = String(status).toLowerCase();

  return normalizedStatus === 'active';
};

const transformAuthorizationType = authType => {
  const normalized = String(authType || '').toUpperCase();

  if (normalized === 'SPECIFIC') {
    return 'PRODUCT SPECIFIC';
  }

  return authType || '--';
};

const mapProducts = item =>
  (item.products ?? [])
    .map(product => String(product.productName ?? '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const getResponseMessage = response => {
  if (typeof response === 'string') return response;
  if (Array.isArray(response)) {
    return response
      .map(item => (typeof item === 'string' ? item : item?.detail || item?.message || item?.title))
      .filter(Boolean)
      .join(' ');
  }
  if (response && typeof response === 'object') {
    return (
      response.detail ||
      response.message ||
      response.title ||
      Object.values(response)
        .filter(value => typeof value === 'string')
        .join(' ')
    );
  }

  return '';
};
const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const getTextOnlyInvalidResponses = invalidResponses => {
  const responses = Array.isArray(invalidResponses)
    ? invalidResponses
    : invalidResponses
    ? [invalidResponses]
    : [];

  return responses
    .map(response => getResponseMessage(response))
    .filter(Boolean)
    .map((message, index) => ({ id: `${message}-${index}`, message }));
};

const getSuccessfulResponseCount = validResponse => {
  const match = String(validResponse || '').match(
    /Successfully\s+(?:activated|deactivated)\s+(\d+)\s+Customer\s+Authorizations?/i
  );

  return match ? Number(match[1]) : null;
};

const getAuthorizationKey = customerAuthorizationId =>
  customerAuthorizationId === null || customerAuthorizationId === undefined
    ? null
    : String(customerAuthorizationId);

const getAuthorizationCountLabel = count => `${count} authorization${count === 1 ? '' : 's'}`;

const getTurnedAuthorizationsMessage = (count, actionLabel) =>
  `${getAuthorizationCountLabel(count)} ${count === 1 ? 'was' : 'were'} turned ${actionLabel}.`;

const getNotTurnedAuthorizationsMessage = (count, actionLabel) =>
  `${getAuthorizationCountLabel(count)} could not be turned ${actionLabel}.`;

const getStructuredBulkActionResult = (response, selectedRows, actionLabel) => {
  const failedAuthorizations = Array.isArray(response?.failedAuthorizations)
    ? response.failedAuthorizations
    : null;
  const successfulCustomerAuthorizationIds = Array.isArray(
    response?.successfulCustomerAuthorizationIds
  )
    ? response.successfulCustomerAuthorizationIds
    : null;

  if (!failedAuthorizations && !successfulCustomerAuthorizationIds) {
    return null;
  }

  const selectedRowsByAuthorizationId = new Map(
    selectedRows.map(row => [getAuthorizationKey(row.customerAuthorizationId), row])
  );
  const failedRowsByAuthorizationId = new Map();

  failedAuthorizations?.forEach(failedAuthorization => {
    const customerAuthorizationId = failedAuthorization?.customerAuthorizationId;
    const authorizationKey = getAuthorizationKey(customerAuthorizationId);
    const row = selectedRowsByAuthorizationId.get(authorizationKey);

    if (row && !failedRowsByAuthorizationId.has(authorizationKey)) {
      failedRowsByAuthorizationId.set(authorizationKey, {
        ...row,
        reason:
          failedAuthorization.reason ||
          failedAuthorization.reasonCode ||
          'The service did not provide a failure reason.',
      });
    }
  });

  const failedAuthorizationKeys = new Set(failedRowsByAuthorizationId.keys());
  const successfulAuthorizationKeys = new Set(
    successfulCustomerAuthorizationIds
      ?.map(getAuthorizationKey)
      .filter(authorizationKey => selectedRowsByAuthorizationId.has(authorizationKey)) || []
  );
  const successfulRows = successfulCustomerAuthorizationIds
    ? selectedRows.filter(row => {
        const authorizationKey = getAuthorizationKey(row.customerAuthorizationId);

        return (
          successfulAuthorizationKeys.has(authorizationKey) &&
          !failedAuthorizationKeys.has(authorizationKey)
        );
      })
    : selectedRows.filter(
        row => !failedAuthorizationKeys.has(getAuthorizationKey(row.customerAuthorizationId))
      );
  const failedRows = Array.from(failedRowsByAuthorizationId.values());
  const unresolvedRows = successfulCustomerAuthorizationIds
    ? selectedRows.filter(row => {
        const authorizationKey = getAuthorizationKey(row.customerAuthorizationId);

        return (
          !successfulAuthorizationKeys.has(authorizationKey) &&
          !failedAuthorizationKeys.has(authorizationKey)
        );
      })
    : [];
  const totalCount = selectedRows.length;
  const failedCount = failedRows.length;
  const successfulCount = successfulRows.length;

  if (!failedCount && !unresolvedRows.length) {
    return {
      actionLabel,
      failedRows,
      status: 'success',
      successfulRows,
    };
  }

  if (!unresolvedRows.length && !successfulCount) {
    return {
      actionLabel,
      failedRows,
      message: getNotTurnedAuthorizationsMessage(totalCount, actionLabel),
      status: 'failed',
      successfulRows,
    };
  }

  if (!unresolvedRows.length) {
    return {
      actionLabel,
      failedRows,
      message: `${getTurnedAuthorizationsMessage(
        successfulCount,
        actionLabel
      )} ${getNotTurnedAuthorizationsMessage(failedCount, actionLabel)}`,
      status: 'partial',
      successfulRows,
    };
  }

  return {
    actionLabel,
    failedRows,
    message: `The service returned an incomplete result. View details for the authorization status.`,
    status: 'failed',
    successfulRows,
    unresolvedCount: unresolvedRows.length,
  };
};

const getTextOnlyBulkActionResult = (response, selectedRows, actionLabel) => {
  const rawErrors = getTextOnlyInvalidResponses(response?.invalidResponses);

  if (!rawErrors.length) {
    return {
      actionLabel,
      failedRows: [],
      status: 'success',
      successfulRows: selectedRows,
    };
  }

  const successfulCount = getSuccessfulResponseCount(response?.validResponse);
  const failedCount = rawErrors.length;
  const totalCount = selectedRows.length;

  if (successfulCount === 0 || (successfulCount === null && failedCount === totalCount)) {
    return {
      actionLabel,
      failedRows: [],
      message: getNotTurnedAuthorizationsMessage(totalCount, actionLabel),
      rawErrors,
      status: 'failed',
      successfulRows: [],
    };
  }

  if (successfulCount !== null && successfulCount > 0 && successfulCount < totalCount) {
    const selectedFailureCount = totalCount - successfulCount;

    return {
      actionLabel,
      failedRows: [],
      message: `${getTurnedAuthorizationsMessage(
        successfulCount,
        actionLabel
      )} ${getNotTurnedAuthorizationsMessage(selectedFailureCount, actionLabel)}`,
      rawErrors,
      status: 'partial',
      successfulRows: [],
    };
  }

  return {
    actionLabel,
    failedRows: [],
    message: `The service could not confirm which authorizations were turned ${actionLabel}. View details for the service response.`,
    rawErrors,
    status: successfulCount > 0 ? 'partial' : 'failed',
    successfulRows: [],
  };
};

const getBulkActionResult = (response, selectedRows, actionLabel) =>
  getStructuredBulkActionResult(response, selectedRows, actionLabel) ||
  getTextOnlyBulkActionResult(response, selectedRows, actionLabel);

const getRequestFailureReason = error =>
  (typeof error?.data === 'string' && error.data) ||
  error?.data?.detail ||
  error?.data?.title ||
  error?.data?.message ||
  error?.detail ||
  error?.title ||
  error?.message ||
  error?.error ||
  (error?.status ? `HTTP ${error.status}` : 'an unexpected error');

const mapApiRowsToGridRows = apiRows =>
  apiRows.map(item => {
    const { customerAuthorizationId } = item;
    const products = mapProducts(item);

    return {
      _raw: item,
      activeStatus: normalizeStatusValue(item.status),
      authorizationType: transformAuthorizationType(item.authorizationType),
      authorizationTypeRaw: String(item.authorizationType ?? item.AuthorizationType ?? '')
        .trim()
        .toUpperCase(),
      class: item.customerClassName ?? '',
      controlType: item.controlType ?? '--',
      customerAuthorizationId,
      customerId: item.customerIdName ?? '--',
      customerName: item.customerName ?? '--',
      destinationCount: item.destinationsCount ?? (item.destinations ?? []).length,
      destinations: item.destinations ?? [],
      id: customerAuthorizationId,
      loadingControlDetails: item.loadingControlDetails ?? [],
      loadingControlNumberCount:
        item.loadingControlNumbersCount ?? (item.loadingControlDetails ?? []).length,
      locationCount: item.locationsCount ?? 0,
      locations: item.locations ?? [],
      productCount: item.productsCount ?? products.length,
      products,
    };
  });

const CustomerAuthorizationsGrid = ({
  appliedFilters = {},
  isProductLoadingManage,
  onEdit,
  onExportingChange,
  onView,
}) => {
  const dispatch = useDispatch();
  const [persistedPageSize, setPersistedPageSize] = usePersistentState(
    PAGE_SIZE_STORAGE_KEY,
    DEFAULT_PAGE_SIZE
  );
  const [toggleCustomerAuthorizationActivation, { isLoading: isActivationUpdating }] =
    useToggleCustomerAuthorizationActivationMutation();
  const [bulkActivateCustomerAuthorizations] = useBulkActivateCustomerAuthorizationsMutation();
  const [bulkDeactivateCustomerAuthorizations] = useBulkDeactivateCustomerAuthorizationsMutation();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: Number.isFinite(Number(persistedPageSize))
      ? Number(persistedPageSize)
      : DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState([{ field: 'customerName', sort: 'asc' }]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bulkActionResult, setBulkActionResult] = useState(null);
  const [isBulkActionResultsDialogOpen, setIsBulkActionResultsDialogOpen] = useState(false);
  const [activeProductRowId, setActiveProductRowId] = useState(null);
  const [activeLocationRowId, setActiveLocationRowId] = useState(null);
  const [activeLoadingControlNumberRowId, setActiveLoadingControlNumberRowId] = useState(null);
  const [activeDestinationRowId, setActiveDestinationRowId] = useState(null);
  const { handlePopoverOpen, handlePopoverClose, forceClose, Popover } = useCustomizablePopover();
  const [triggerExportList] = useLazyGetCustomerAuthorizationsExportListQuery();
  const [isExporting, setIsExporting] = useState(false);
  const {
    handlePopoverOpen: handlePopoverOpenLocations,
    handlePopoverClose: handlePopoverCloseLocations,
    forceClose: forceCloseLocations,
    Popover: PopoverLocations,
  } = useCustomizablePopover();
  const {
    handlePopoverOpen: handlePopoverOpenLoadingControlNumbers,
    handlePopoverClose: handlePopoverCloseLoadingControlNumbers,
    forceClose: forceCloseLoadingControlNumbers,
    Popover: PopoverLoadingControlNumbers,
  } = useCustomizablePopover();
  const {
    handlePopoverOpen: handlePopoverOpenDestinations,
    handlePopoverClose: handlePopoverCloseDestinations,
    forceClose: forceCloseDestinations,
    Popover: PopoverDestinations,
  } = useCustomizablePopover();

  const rowCacheRef = useRef(new Map());
  const activeSort = sortModel[0] || {};

  useEffect(() => {
    setPersistedPageSize(paginationModel.pageSize);
  }, [paginationModel.pageSize, setPersistedPageSize]);

  const prevFilterKeyRef = useRef(JSON.stringify(appliedFilters));

  useEffect(() => {
    const currentKey = JSON.stringify(appliedFilters);
    if (currentKey !== prevFilterKeyRef.current) {
      rowCacheRef.current.clear();
      setRowSelectionModel([]);
      if (paginationModel?.page !== 0) {
        setPaginationModel(previousModel => ({ ...previousModel, page: 0 }));
      }
      prevFilterKeyRef.current = currentKey;
    }
  }, [appliedFilters, paginationModel?.page]);

  const queryParams = useMemo(() => {
    return {
      ...paginationModel,
      sortColumn: SORT_COLUMN_MAP[activeSort.field] || 'CustomerName',
      sortDirection: activeSort.sort === 'desc' ? 'Descending' : 'Ascending',
      ...(appliedFilters.affiliate?.customerIdName && {
        Supplier: {
          CompanyCustomerIdName: appliedFilters.affiliate.customerIdName,
          DataCompanyId: appliedFilters.affiliate.dataCompanyId,
        },
      }),
      ...(appliedFilters.locations?.length && {
        locationIdName: appliedFilters.locations.map(location => location.value),
      }),
      ...(appliedFilters.products?.length && {
        productIdName: appliedFilters.products.map(product => product.value),
      }),
      ...(appliedFilters.controlTypes?.length && {
        controlType: appliedFilters.controlTypes.map(controlType => controlType.value),
      }),
      ...(appliedFilters.authTypes?.length && {
        authorizationType: appliedFilters.authTypes.map(authType => authType.value),
      }),
      ...(appliedFilters.status &&
        appliedFilters.status !== 'ALLSTATUSES' && { authorizationStatus: appliedFilters.status }),
      ...(appliedFilters.search && { quickFilter: appliedFilters.search }),
    };
  }, [
    paginationModel,
    activeSort.field,
    activeSort.sort,
    appliedFilters.affiliate?.customerIdName,
    appliedFilters.affiliate?.dataCompanyId,
    appliedFilters.locations,
    appliedFilters.products,
    appliedFilters.controlTypes,
    appliedFilters.authTypes,
    appliedFilters.status,
    appliedFilters.search,
  ]);

  const { currentData: customerAuthData = {}, isFetching: loading } =
    useGetCustomerAuthorizationsListQuery(
      { params: queryParams },
      {
        skip: !appliedFilters.affiliate?.customerIdName,
      }
    );
  const rows = useMemo(
    () =>
      mapApiRowsToGridRows(
        customerAuthData.CustomerAuthorizations || customerAuthData.customerAuthorizations || []
      ),
    [customerAuthData.CustomerAuthorizations, customerAuthData.customerAuthorizations]
  );
  const rowCount = customerAuthData.TotalResults || customerAuthData.totalResults || 0;
  const authorizationCount = loading ? 0 : rowCount;
  const isExportDisabled = isExporting || authorizationCount === 0;

  const activeLocationRow = rows.find(row => row.customerAuthorizationId === activeLocationRowId);
  const locationsList = activeLocationRow?.locations ?? [];
  const isLocationDataUnavailable =
    (activeLocationRow?.locationCount ?? 0) > 0 && locationsList.length === 0;
  const visibleLocations = locationsList.slice(0, MAX_VISIBLE_LOCATIONS);
  const remainingLocationCount = locationsList.length - MAX_VISIBLE_LOCATIONS;
  const activeLoadingControlNumberRow = rows.find(
    row => row.customerAuthorizationId === activeLoadingControlNumberRowId
  );
  const loadingControlDetailsList = activeLoadingControlNumberRow?.loadingControlDetails ?? [];
  const isLoadingControlNumberDataUnavailable =
    (activeLoadingControlNumberRow?.loadingControlNumberCount ?? 0) > 0 &&
    loadingControlDetailsList.length === 0;
  const visibleLoadingControlNumbers = loadingControlDetailsList.slice(
    0,
    MAX_VISIBLE_LOADING_CONTROL_NUMBERS
  );
  const remainingLoadingControlNumberCount =
    loadingControlDetailsList.length - MAX_VISIBLE_LOADING_CONTROL_NUMBERS;
  const activeDestinationRow = rows.find(
    row => row.customerAuthorizationId === activeDestinationRowId
  );
  const destinationsList = activeDestinationRow?.destinations ?? [];
  const isDestinationDataUnavailable =
    (activeDestinationRow?.destinationCount ?? 0) > 0 && destinationsList.length === 0;
  const visibleDestinations = destinationsList.slice(0, MAX_VISIBLE_DESTINATIONS);
  const remainingDestinationCount = destinationsList.length - MAX_VISIBLE_DESTINATIONS;

  useEffect(() => {
    rows.forEach(row => {
      rowCacheRef.current.set(row.id, row);
    });
  }, [rows]);

  const currentPageRowIds = useMemo(() => rows.map(row => row.id), [rows]);
  const selectedCount = rowSelectionModel.length;
  const hasSelections = selectedCount > 0;

  const currentPageSelection = useMemo(
    () => rowSelectionModel.filter(id => currentPageRowIds.includes(id)),
    [currentPageRowIds, rowSelectionModel]
  );

  const handleRowSelectionModelChange = useCallback(
    newSelectionModel => {
      const currentPageIdSet = new Set(currentPageRowIds);
      const otherPageSelections = rowSelectionModel.filter(id => !currentPageIdSet.has(id));
      const currentPageSelections = newSelectionModel.filter(id => currentPageIdSet.has(id));

      setRowSelectionModel([...otherPageSelections, ...currentPageSelections]);
    },
    [currentPageRowIds, rowSelectionModel]
  );

  const handleSortModelChange = newSortModel => {
    const nextSortModel =
      newSortModel && newSortModel.length ? newSortModel : [{ field: 'customerName', sort: 'asc' }];
    setSortModel(nextSortModel);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const paginationStatusDisplay = useMemo(() => {
    const totalDisplay = formatNumber(authorizationCount, { defaultValue: 0 });

    if (hasSelections) {
      return `${formatNumber(selectedCount, {
        defaultValue: 0,
      })} of ${totalDisplay} Authorizations`;
    }

    return `${totalDisplay} Authorizations`;
  }, [authorizationCount, hasSelections, selectedCount]);

  const runBulkAction = useCallback(
    async (isActivation, selectedRows, actionLabel) => {
      const customerAuthorizationIds = selectedRows.map(row => row.customerAuthorizationId);
      const response = await (isActivation
        ? bulkActivateCustomerAuthorizations({ customerAuthorizationIds }).unwrap()
        : bulkDeactivateCustomerAuthorizations({ customerAuthorizationIds }).unwrap());

      return getBulkActionResult(response, selectedRows, actionLabel);
    },
    [bulkActivateCustomerAuthorizations, bulkDeactivateCustomerAuthorizations]
  );

  useEffect(() => {
    onExportingChange(isExporting);
  }, [isExporting, onExportingChange]);

  const handleExportAllRows = useCallback(async () => {
    if (isExportDisabled) {
      return;
    }

    setIsExporting(true);
    try {
      const { page, pageSize, ...exportQueryParams } = queryParams;

      const result = await triggerExportList(exportQueryParams, false);

      if (result?.error) {
        throw result.error;
      }

      const csvBlob =
        result?.data instanceof Blob
          ? result.data
          : new Blob([result?.data || ''], { type: 'text/csv;charset=utf-8;' });
      const filename = 'customer-authorizations.csv';

      downloadBlob(csvBlob, filename);

      dispatch(
        createSnack({
          message: 'Export has been successfully downloaded.',
          options: { variant: 'success' },
          title: 'Action Completed',
        })
      );
    } catch (error) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.title ||
        error?.error ||
        'Due to an error, your customer authorizations could not be exported to a .CSV file.';

      dispatch(
        createSnack({
          message: errorMessage,
          options: { persist: true, variant: 'error' },
          title: 'Action Not Completed',
        })
      );
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [dispatch, isExportDisabled, queryParams, triggerExportList]);

  const handleBulkAction = useCallback(
    action => {
      const isActivation = action === 'activate';
      const actionLabel = isActivation ? 'on' : 'off';
      const actionVerb = isActivation ? 'activate' : 'deactivate';
      const selectedRows = rowSelectionModel
        .map(rowId => rowCacheRef.current.get(rowId))
        .filter(
          row =>
            row.customerAuthorizationId !== null &&
            row.customerAuthorizationId !== undefined &&
            row.customerAuthorizationId !== ''
        );

      if (!selectedRows.length) {
        dispatch(
          createSnack({
            message: 'The selected authorizations could not be found. Please select them again.',
            options: { persist: true, variant: 'error' },
            title: 'Action Not Completed',
          })
        );
        return;
      }

      const customerAuthorizationIds = selectedRows.map(row => row.customerAuthorizationId);

      dispatch(
        openConfirmationDialog({
          confirmAction: async () => {
            try {
              setIsProcessing(true);
              const result = await runBulkAction(isActivation, selectedRows, actionLabel);
              const isSingleAuthorization = selectedRows.length === 1;

              if (result.status === 'success') {
                dispatch(
                  createSnack({
                    message: isSingleAuthorization
                      ? `Authorization has been successfully turned ${actionLabel}.`
                      : `Authorizations have been successfully turned ${actionLabel}.`,
                    options: { autoHideDuration: 4000, variant: 'success' },
                    title: 'Action Completed',
                  })
                );
                setRowSelectionModel([]);
                return;
              }

              setBulkActionResult(result);
              dispatch(
                createSnack({
                  message: result.message,
                  options: {
                    content: snackbarKey => (
                      <CustomerAuthorizationBulkSnackbar
                        message={result.message}
                        onClose={() => dispatch(closeSnack(snackbarKey))}
                        onViewDetails={() => setIsBulkActionResultsDialogOpen(true)}
                        title={
                          result.status === 'partial'
                            ? 'Action Partially Completed'
                            : 'Action Not Completed'
                        }
                        variant={result.status === 'partial' ? 'warning' : 'error'}
                      />
                    ),
                    persist: true,
                    variant: result.status === 'partial' ? 'warning' : 'error',
                  },
                })
              );

              if (!result.rawErrors) {
                setRowSelectionModel(result.failedRows.map(row => row.id));
              }
            } catch (error) {
              const reason = String(getRequestFailureReason(error)).replace(/[.\s]+$/, '');

              dispatch(
                createSnack({
                  message: `Due to ${reason}, ${getNotTurnedAuthorizationsMessage(
                    selectedRows.length,
                    actionLabel
                  )}`,
                  options: { persist: true, variant: 'error' },
                  title: 'Action Not Completed',
                })
              );
            } finally {
              setIsProcessing(false);
            }
          },
          header: `Turn ${isActivation ? 'On' : 'Off'} ${
            customerAuthorizationIds.length
          } Authorizations`,
          message: `Are you sure you want to ${actionVerb} the selected authorizations?`,
          params: {},
        })
      );
    },
    [dispatch, rowSelectionModel, runBulkAction]
  );

  const CustomActionFooter = useCallback(
    () => (
      <BulkActionFooter
        actions={[
          <MenuItem onClick={() => handleBulkAction('activate')}>Turn on all selected</MenuItem>,
          <MenuItem onClick={() => handleBulkAction('deactivate')}>Turn off all selected</MenuItem>,
        ]}
        disabled={!hasSelections || isProcessing}
        primaryActionButtonTitle="Bulk Actions"
      />
    ),
    [handleBulkAction, hasSelections, isProcessing]
  );

  const notifyActionNotCompleted = useCallback(
    message => {
      dispatch(
        createSnack({
          message,
          options: { variant: 'error' },
          title: 'Action Not Completed',
        })
      );
    },
    [dispatch]
  );

  const confirmToggleCustomerAuthActivation = useCallback(
    async row => {
      const isActive = row.activeStatus === true;
      const customerAuthorizationId = row?.customerAuthorizationId;
      const successMessage = `Authorization has been successfully turned ${
        isActive ? 'off' : 'on'
      }.`;
      try {
        await toggleCustomerAuthorizationActivation({
          customerAuthorizationId,
          isActive,
        }).unwrap();
        dispatch(
          createSnack({
            message: successMessage,
            options: { autoHideDuration: 4000, variant: 'success' },
            title: 'Action Completed',
          })
        );
      } catch (error) {
        const errorMessage =
          (typeof error?.data === 'string' && error?.data) ||
          error?.data?.detail ||
          error?.data?.title ||
          error?.error ||
          `Due to an error, the authorization could not be turned ${isActive ? 'off' : 'on'}.`;
        notifyActionNotCompleted(errorMessage);
      }
    },
    [dispatch, notifyActionNotCompleted, toggleCustomerAuthorizationActivation]
  );

  const handleActivationSwitchChange = useCallback(
    row => {
      const isActive = row.activeStatus === true;
      const action = isActive ? 'off' : 'on';
      const reverseAction = isActive ? 'on' : 'off';

      dispatch(
        openConfirmationDialog({
          confirmAction: () => confirmToggleCustomerAuthActivation(row),
          header: `${isActive ? 'Deactivate' : 'Activate'} Authorization?`,
          message: `Turn ${action} customer loading for  ${row.customerId} ${row.customerName}? Customer Authorizations can be turned back ${reverseAction} at any time.`,
        })
      );
    },
    [confirmToggleCustomerAuthActivation, dispatch]
  );

  const columns = useMemo(
    () => [
      {
        field: 'activeStatus',
        flex: 0.8,
        headerName: 'Status',
        minWidth: 120,
        renderCell: ({ row }) => {
          return (
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={row.activeStatus}
                    disabled={!isProductLoadingManage || isActivationUpdating || isProcessing}
                    onChange={() => handleActivationSwitchChange(row)}
                    size="medium"
                  />
                }
                label={row.activeStatus ? 'On' : 'Off'}
                labelPlacement="start"
                sx={{ justifyContent: 'center', m: 0, width: '100%' }}
              />
            </FormGroup>
          );
        },
      },
      { field: 'customerId', flex: 0.8, headerName: 'Customer Id', minWidth: 150 },
      { field: 'customerName', flex: 1.2, headerName: 'Customer Name', minWidth: 200 },
      { field: 'controlType', flex: 1.2, headerName: 'Control Type', minWidth: 180 },
      { field: 'authorizationType', flex: 1.1, headerName: 'Auth Type', minWidth: 180 },
      {
        field: 'locationCount',
        flex: 0.8,
        headerName: 'Locations',
        minWidth: 130,
        renderCell: ({ row }) => {
          const count = row.locationCount ?? 0;

          if (count === 0) {
            return <Typography variant="body2">0</Typography>;
          }

          return (
            <Box
              onMouseEnter={e => {
                setActiveLocationRowId(row.customerAuthorizationId);
                handlePopoverOpenLocations(e);
              }}
              onMouseLeave={e => handlePopoverCloseLocations(e)}
              sx={{ cursor: 'pointer', display: 'inline-flex', py: 1 }}
            >
              <Typography sx={{ pointerEvents: 'none', textUnderlineOffset: '2px' }} variant="link">
                {count}
              </Typography>
            </Box>
          );
        },
        sortable: false,
      },
      {
        field: 'productCount',
        flex: 0.8,
        headerName: 'Products',
        minWidth: 130,
        renderCell: ({ row }) => {
          if (row.authorizationTypeRaw !== 'SPECIFIC') {
            return <Typography variant="body2">ALL</Typography>;
          }

          if (!row.productCount) {
            return <Typography variant="body2">0</Typography>;
          }

          return (
            <Box
              onMouseEnter={e => {
                setActiveProductRowId(row.id);
                handlePopoverOpen(e);
              }}
              onMouseLeave={e => handlePopoverClose(e)}
              sx={{ cursor: 'pointer', display: 'inline-flex', py: 1 }}
            >
              <Typography sx={{ pointerEvents: 'none', textUnderlineOffset: '2px' }} variant="link">
                {row.productCount}
              </Typography>
            </Box>
          );
        },
        sortable: false,
      },
      {
        field: 'loadingControlNumberCount',
        flex: 1,
        headerName: 'LCNs',
        minWidth: 220,
        renderCell: ({ row }) => {
          const count = row.loadingControlNumberCount ?? 0;

          if (count === 0) {
            return <Typography variant="body2">0</Typography>;
          }

          return (
            <Box
              onMouseEnter={e => {
                setActiveLoadingControlNumberRowId(row.customerAuthorizationId);
                handlePopoverOpenLoadingControlNumbers(e);
              }}
              onMouseLeave={e => handlePopoverCloseLoadingControlNumbers(e)}
              sx={{ cursor: 'pointer', display: 'inline-flex', py: 1 }}
            >
              <Typography sx={{ pointerEvents: 'none', textUnderlineOffset: '2px' }} variant="link">
                {count}
              </Typography>
            </Box>
          );
        },
        sortable: false,
      },
      {
        field: 'destinationCount',
        flex: 0.9,
        headerName: 'Destinations',
        minWidth: 150,
        renderCell: ({ row }) => {
          const count = row.destinationCount ?? 0;

          if (count === 0) {
            return <Typography variant="body2">0</Typography>;
          }

          return (
            <Box
              onMouseEnter={e => {
                setActiveDestinationRowId(row.customerAuthorizationId);
                handlePopoverOpenDestinations(e);
              }}
              onMouseLeave={e => handlePopoverCloseDestinations(e)}
              sx={{ cursor: 'pointer', display: 'inline-flex', py: 1 }}
            >
              <Typography sx={{ pointerEvents: 'none', textUnderlineOffset: '2px' }} variant="link">
                {count}
              </Typography>
            </Box>
          );
        },
        sortable: false,
      },
      { field: 'class', flex: 0.7, headerName: 'Class', minWidth: 100 },
      {
        field: 'actions',
        flex: 0.9,
        headerName: 'Actions',
        minWidth: 150,
        renderCell: ({ row }) =>
          isProductLoadingManage ? (
            <ButtonGroup orientation="horizontal" size="small" variant="outlined">
              <Button onClick={() => onEdit(row.customerAuthorizationId)}>Edit</Button>
              <MDropdownMenuButton variant="outlined">
                <MenuItem onClick={() => onView(row.customerAuthorizationId)}>View</MenuItem>
              </MDropdownMenuButton>
            </ButtonGroup>
          ) : (
            <Button
              onClick={() => onView(row.customerAuthorizationId)}
              size="small"
              variant="outlined"
            >
              View
            </Button>
          ),
        sortable: false,
      },
    ],
    [
      handleActivationSwitchChange,
      handlePopoverClose,
      handlePopoverOpen,
      handlePopoverCloseLocations,
      handlePopoverOpenLocations,
      handlePopoverCloseLoadingControlNumbers,
      handlePopoverOpenLoadingControlNumbers,
      handlePopoverCloseDestinations,
      handlePopoverOpenDestinations,
      isActivationUpdating,
      isProcessing,
      isProductLoadingManage,
      onEdit,
      onView,
    ]
  );

  const activeProductRow = rows.find(row => row.id === activeProductRowId);
  const popoverProducts = activeProductRow?.products || [];
  const areProductsUnavailable =
    Number(activeProductRow?.productCount) > 0 && popoverProducts.length === 0;
  const visibleProducts = popoverProducts.slice(0, MAX_VISIBLE_PRODUCTS);
  const remainingProductCount = popoverProducts.length - MAX_VISIBLE_PRODUCTS;

  const renderBulkActionResultsDialog = () => {
    if (!bulkActionResult) {
      return null;
    }

    const { actionLabel, failedRows, rawErrors, successfulRows, unresolvedCount } =
      bulkActionResult;
    const getCustomerLabel = row =>
      row.customerName ? `${row.customerId} — ${row.customerName}` : row.customerId;

    return (
      <MDialog
        fullWidth
        header="Bulk Action Results"
        maxWidth="md"
        onClose={() => setIsBulkActionResultsDialogOpen(false)}
        open={isBulkActionResultsDialogOpen}
        rightButtonAction={() => setIsBulkActionResultsDialogOpen(false)}
        rightButtonText="Close"
        sx={{
          '& .MuiDialog-paper': { maxHeight: '80%' },
          '& .MuiDialogContent-root': { overflow: 'hidden' },
        }}
      >
        <Box
          data-testid="bulk-action-results-content"
          sx={{ maxHeight: '60vh', overflowY: 'auto' }}
        >
          {rawErrors?.length ? (
            <>
              <Box data-testid="raw-bulk-action-errors" sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {rawErrors.map(error => (
                  <Typography
                    key={error.id}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}
                    variant="body2"
                  >
                    {error.message}
                  </Typography>
                ))}
              </Box>
            </>
          ) : (
            <>
              <Typography component="h3" sx={{ mb: 1 }} variant="subtitle1">
                Turned {actionLabel} ({successfulRows.length})
              </Typography>
              <Box component="table" sx={{ borderCollapse: 'collapse', mb: 3, width: '100%' }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" scope="col" sx={{ pb: 1, textAlign: 'left' }}>
                      Authorization
                    </Box>
                    <Box component="th" scope="col" sx={{ pb: 1, textAlign: 'left' }}>
                      Customer
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {successfulRows.map(row => (
                    <Box component="tr" key={row.id}>
                      <Box component="td" sx={{ py: 1 }}>
                        {row.customerAuthorizationId}
                      </Box>
                      <Box component="td" sx={{ py: 1 }}>
                        {getCustomerLabel(row)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Typography component="h3" sx={{ mb: 1 }} variant="subtitle1">
                Not turned {actionLabel} ({failedRows.length})
              </Typography>
              <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" scope="col" sx={{ pb: 1, textAlign: 'left' }}>
                      Authorization
                    </Box>
                    <Box component="th" scope="col" sx={{ pb: 1, textAlign: 'left' }}>
                      Customer
                    </Box>
                    <Box component="th" scope="col" sx={{ pb: 1, textAlign: 'left' }}>
                      Reason
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {failedRows.map(row => (
                    <Box component="tr" key={row.id}>
                      <Box component="td" sx={{ py: 1 }}>
                        {row.customerAuthorizationId}
                      </Box>
                      <Box component="td" sx={{ py: 1 }}>
                        {getCustomerLabel(row)}
                      </Box>
                      <Box component="td" sx={{ py: 1 }}>
                        {row.reason}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              {unresolvedCount ? (
                <Typography sx={{ mt: 2 }} variant="body2">
                  The service did not return a result for {unresolvedCount} selected authorizations.
                </Typography>
              ) : null}
            </>
          )}
        </Box>
      </MDialog>
    );
  };

  return (
    <>
      <PersistentServerSideDataGrid
        bulkActionFooter={isProductLoadingManage ? CustomActionFooter : null}
        checkboxSelection={isProductLoadingManage}
        columns={columns}
        defaultPageSize={paginationModel.pageSize}
        disableColumnMenu
        disableColumnReorder={isExporting}
        disabledExport={isExportDisabled}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        id="customerAuthorizationsGrid"
        isExporting={isExporting}
        keepNonExistentRowsSelected
        loading={loading || isProcessing || isExporting}
        onExportAllRows={handleExportAllRows}
        onPaginationChange={setPaginationModel}
        onResetView={() => setRowSelectionModel([])}
        onRowSelectionModelChange={
          isProductLoadingManage ? handleRowSelectionModelChange : undefined
        }
        onSortChange={handleSortModelChange}
        paginationStatus={paginationStatusDisplay}
        rows={rows}
        rowSelectionModel={isProductLoadingManage ? currentPageSelection : undefined}
        totalRows={authorizationCount}
      />
      <Popover
        id="customer-auth-products-popover"
        popoverFooterContent={
          remainingProductCount > 0 && (
            <Typography
              onClick={() => {
                forceClose();
                onView(activeProductRow?.customerAuthorizationId);
              }}
              sx={{ cursor: 'pointer', textUnderlineOffset: '2px' }}
              variant="link"
            >
              + {remainingProductCount} More
            </Typography>
          )
        }
        popoverHeaderContent={<Typography variant="body1bold">Products</Typography>}
        popoverMainContent={
          areProductsUnavailable ? (
            <Typography color="error" variant="body2">
              Unable to retrieve products
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleProducts.map(productName => (
                <Typography key={productName} sx={POPOVER_TEXT_WITH_MAX_WIDTH_SX} variant="body1">
                  {productName}
                </Typography>
              ))}
            </Stack>
          )
        }
      />
      <PopoverLocations
        id="location-count-popover"
        popoverFooterContent={
          remainingLocationCount > 0 && (
            <Typography
              onClick={() => {
                forceCloseLocations();
                onView(activeLocationRowId);
              }}
              sx={{ cursor: 'pointer', textUnderlineOffset: '2px' }}
              variant="link"
            >
              + {remainingLocationCount} More
            </Typography>
          )
        }
        popoverHeaderContent={<Typography variant="body1bold">Locations</Typography>}
        popoverMainContent={
          isLocationDataUnavailable ? (
            <Typography color="error" variant="body2">
              Unable to retrieve locations
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleLocations.map(location => (
                <Typography
                  key={location.locationName}
                  sx={POPOVER_TEXT_WITH_MAX_WIDTH_SX}
                  variant="body1"
                >
                  {location.locationName}
                </Typography>
              ))}
            </Stack>
          )
        }
      />
      <PopoverLoadingControlNumbers
        id="loading-control-number-count-popover"
        popoverFooterContent={
          remainingLoadingControlNumberCount > 0 && (
            <Typography
              onClick={() => {
                forceCloseLoadingControlNumbers();
                onView(activeLoadingControlNumberRowId);
              }}
              sx={{ cursor: 'pointer', textUnderlineOffset: '2px' }}
              variant="link"
            >
              + {remainingLoadingControlNumberCount} More
            </Typography>
          )
        }
        popoverHeaderContent={<Typography variant="body1bold">Loading Control Numbers</Typography>}
        popoverMainContent={
          isLoadingControlNumberDataUnavailable ? (
            <Typography color="error" variant="body2">
              Unable to retrieve loading control numbers
            </Typography>
          ) : (
            <Box
              sx={{
                alignItems: 'center',
                columnGap: 1,
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(0, 1fr)',
                rowGap: 1,
              }}
            >
              <Typography color="text.secondary" fontWeight={700} variant="body2">
                LCN
              </Typography>
              <Typography color="text.secondary" fontWeight={700} variant="body2">
                Destination
              </Typography>
              {visibleLoadingControlNumbers.map(loadingControlDetails => (
                <React.Fragment
                  key={`${loadingControlDetails.loadingControlNumber}-${loadingControlDetails.globalDestinationName}`}
                >
                  <Typography sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {loadingControlDetails.loadingControlNumber}
                  </Typography>
                  <Tooltip
                    arrow
                    title={loadingControlDetails.globalDestinationName || 'NO LINKED DESTINATION'}
                  >
                    <Typography sx={POPOVER_TEXT_WITH_MAX_WIDTH_SX}>
                      {loadingControlDetails.globalDestinationName || 'NO LINKED DESTINATION'}
                    </Typography>
                  </Tooltip>
                </React.Fragment>
              ))}
            </Box>
          )
        }
      />
      <PopoverDestinations
        id="destination-count-popover"
        popoverFooterContent={
          remainingDestinationCount > 0 && (
            <Typography
              onClick={() => {
                forceCloseDestinations();
                onView(activeDestinationRowId);
              }}
              sx={{ cursor: 'pointer', textUnderlineOffset: '2px' }}
              variant="link"
            >
              + {remainingDestinationCount} More
            </Typography>
          )
        }
        popoverHeaderContent={<Typography variant="body1bold">Destinations</Typography>}
        popoverMainContent={
          isDestinationDataUnavailable ? (
            <Typography color="error" variant="body2">
              Unable to retrieve destinations
            </Typography>
          ) : (
            <Stack spacing={1}>
              <Box
                sx={{
                  alignItems: 'center',
                  columnGap: 3,
                  display: 'grid',
                  gridTemplateColumns: '160px 40px',
                }}
              >
                <Typography color="text.secondary" fontWeight={700} variant="body2">
                  Destination
                </Typography>
                <Typography color="text.secondary" fontWeight={700} variant="body2">
                  State
                </Typography>
              </Box>
              {visibleDestinations.map(destination => (
                <Box
                  key={`${destination.destinationName}-${destination.destinationState}`}
                  sx={{
                    alignItems: 'center',
                    columnGap: 3,
                    display: 'grid',
                    gridTemplateColumns: '160px 40px',
                  }}
                >
                  <Tooltip arrow title={destination.destinationName}>
                    <Typography sx={POPOVER_TEXT_SX}>
                      {destination.destinationName || 'NO LINKED DESTINATION'}
                    </Typography>
                  </Tooltip>
                  <Typography sx={POPOVER_TEXT_SX}>
                    {destination.destinationState || 'NO LINKED DESTINATION'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )
        }
      />
      {renderBulkActionResultsDialog()}
    </>
  );
};

CustomerAuthorizationsGrid.propTypes = {
  appliedFilters: shape({}),
  isProductLoadingManage: bool.isRequired,
  onEdit: func.isRequired,
  onExportingChange: func,
  onView: func.isRequired,
};

CustomerAuthorizationsGrid.defaultProps = {
  appliedFilters: {},
  onExportingChange: () => {},
};

export default CustomerAuthorizationsGrid;
